import React from 'react';
import { motion, useReducedMotion } from 'framer-motion';
import { methodSystems, osteopathyPrinciples, kinesiologyContent } from '../mockData';
import { Card, CardContent } from './ui/card';
import PhotoCredit from './ui/PhotoCredit';
import { Check, Sparkles, Brain, Heart, Activity, BookOpen, Leaf } from 'lucide-react';
import { createStaggerContainer, fadeInUp, getScrollAnimationProps, scaleIn } from '../lib/animations';

const MethodSection = () => {
  const shouldReduceMotion = useReducedMotion();
  const scrollAnimationProps = getScrollAnimationProps(shouldReduceMotion, { amount: 0.05 });
  const containerVariants = shouldReduceMotion ? undefined : createStaggerContainer({ staggerChildren: 0.12, delayChildren: 0.05 });

  return (
    <motion.section
      id="methode"
      className="py-12 sm:py-16 lg:py-20 bg-gradient-to-b from-[#F5F0E8] via-[#F8F4ED] to-[#F5F0E8]"
      {...scrollAnimationProps}
      variants={containerVariants}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Section Title avec accent terracotta */}
        <motion.div className="text-center mb-10 sm:mb-16 lg:mb-20" variants={createStaggerContainer({ staggerChildren: 0.1 })}>
          <motion.div className="flex items-center justify-center gap-3 mb-4" variants={fadeInUp}>
            <motion.span
              className="h-px w-12 bg-gradient-to-r from-transparent to-[#a5400e]"
              initial={{ width: 0 }}
              whileInView={{ width: 48 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            />
            <span className="text-xs sm:text-sm uppercase tracking-[0.3em] text-[#a5400e] font-medium">
              Approche Holistique
            </span>
            <motion.span
              className="h-px w-12 bg-gradient-to-l from-transparent to-[#a5400e]"
              initial={{ width: 0 }}
              whileInView={{ width: 48 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
            />
          </motion.div>

          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl xl:text-6xl font-serif text-[#5c2810] mb-4 sm:mb-6 relative inline-block"
            variants={fadeInUp}
          >
            Ma Méthode
            <motion.span
              className="absolute -bottom-2 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#a5400e] to-transparent rounded-full"
              initial={{ scaleX: 0, opacity: 0 }}
              whileInView={{ scaleX: 1, opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.3 }}
            />
          </motion.h2>

          <motion.p className="text-sm sm:text-base lg:text-lg text-[#7a4a3a] max-w-3xl mx-auto leading-relaxed" variants={fadeInUp}>
            Ma pratique se concentre sur une approche globale du corps, en considérant les interactions
            et synergies entre différents systèmes pour optimiser votre santé et votre bien-être.
          </motion.p>
        </motion.div>

        {/* Systems Grid - Cards avec effet glassmorphism */}
        <motion.div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6 lg:gap-8 mb-12 sm:mb-16 lg:mb-20" variants={containerVariants}>
          {methodSystems.map((system, index) => {
            const icons = [Activity, Brain, Sparkles, Heart];
            const IconComponent = icons[index % icons.length];

            return (
              <motion.div
                key={system.id}
                variants={scaleIn}
                whileHover={shouldReduceMotion ? undefined : { y: -6 }}
                transition={{ duration: 0.2 }}
              >
                <Card className="group h-full border-none bg-white/80 backdrop-blur-sm rounded-2xl overflow-hidden shadow-[0_4px_15px_rgb(0,0,0,0.06)] sm:shadow-[0_8px_30px_rgb(0,0,0,0.04)] hover:shadow-[0_8px_30px_rgb(165,64,14,0.12)] transition-all duration-300">
                  <CardContent className="p-0 relative">
                    {/* Image Container avec overlay terracotta */}
                    <div className="relative overflow-hidden aspect-[1.45/1] bg-[#F5F0E8]">
                      <img
                        src={system.image}
                        alt={system.name}
                        className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                        loading="lazy"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-[#5c2810]/30 via-transparent to-transparent" />

                      {/* Icône */}
                      <div className="absolute top-3 right-3 w-10 h-10 rounded-full bg-white/30 backdrop-blur-sm flex items-center justify-center">
                        <IconComponent className="text-white" size={20} />
                      </div>
                      <PhotoCredit credit={system.credit} />
                    </div>

                    {/* Content */}
                    <div className="p-4 sm:p-5 lg:p-6">
                      <h3 className="text-base sm:text-lg lg:text-xl font-serif text-[#5c2810] mb-2 group-hover:text-[#a5400e] transition-colors duration-300">
                        {system.name}
                      </h3>
                      <p className="text-xs sm:text-sm text-[#7a4a3a] leading-relaxed">
                        {system.description}
                      </p>
                    </div>

                    {/* Hover border effect */}
                    <div className="absolute inset-0 rounded-2xl border-2 border-transparent group-hover:border-[#a5400e]/20 transition-colors duration-300 pointer-events-none" />
                  </CardContent>
                </Card>
              </motion.div>
            );
          })}
        </motion.div>

        {/* Section Comprendre l'ostéopathie - Design Terracotta Premium */}
        <motion.div
          className="relative mb-8 sm:mb-12"
          initial={shouldReduceMotion ? undefined : { opacity: 0, y: 20 }}
          whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5 }}
        >
          {/* Background decoratif */}
          <div className="absolute -inset-3 sm:-inset-6 bg-gradient-to-br from-[#a5400e]/5 via-[#d46a3e]/5 to-[#e8a87c]/5 rounded-2xl sm:rounded-3xl blur-2xl" />

          <div className="relative bg-white/70 backdrop-blur-md p-5 sm:p-8 lg:p-10 xl:p-12 rounded-2xl sm:rounded-3xl border border-[#a5400e]/10 shadow-[0_20px_60px_rgb(165,64,14,0.08)]">
            {/* Header avec ligne décorative */}
            <div className="flex items-center gap-3 sm:gap-4 mb-6 sm:mb-8">
              <div className="w-1 h-10 sm:h-12 bg-gradient-to-b from-[#a5400e] to-[#e8a87c] rounded-full shrink-0" />
              <div>
                <span className="text-xs uppercase tracking-[0.2em] text-[#a5400e]/70 font-medium">
                  Découvrir
                </span>
                <h3 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-serif text-[#a5400e] mt-1">
                  Comprendre l'ostéopathie neuro-endocrinienne
                </h3>
              </div>
            </div>

            {/* Content en 2 colonnes sur desktop */}
            <div className="grid lg:grid-cols-2 gap-5 sm:gap-8 lg:gap-10">
              <div>
                <p className="text-sm sm:text-base lg:text-lg text-[#5c2810] leading-relaxed mb-4">
                  L'ostéopathie neuro-endocrinienne est une approche thérapeutique qui considère les
                  <span className="text-[#a5400e] font-medium"> interconnexions complexes</span> entre le système nerveux,
                  le système hormonal et la structure physique du corps.
                </p>
                <p className="text-xs sm:text-sm lg:text-base text-[#7a4a3a] leading-relaxed">
                  Cette méthode reconnaît que les déséquilibres dans un système peuvent affecter les autres, créant ainsi
                  des cascades de symptômes qui peuvent sembler sans rapport. En rétablissant l'harmonie entre ces systèmes,
                  le corps retrouve sa capacité naturelle à s'autoréguler.
                </p>
              </div>

              <div className="bg-gradient-to-br from-[#a5400e]/5 to-[#e8a87c]/5 p-4 sm:p-5 lg:p-6 rounded-xl sm:rounded-2xl border border-[#a5400e]/10">
                <h4 className="text-sm sm:text-base lg:text-lg font-serif text-[#a5400e] mb-3 flex items-center gap-2">
                  <Sparkles size={18} />
                  Principes fondamentaux
                </h4>
                <div className="space-y-2 sm:space-y-3">
                  {osteopathyPrinciples.map((principle, index) => (
                    <div
                      key={index}
                      className="flex items-start space-x-3 group"
                    >
                      <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-[#a5400e]/10 flex items-center justify-center flex-shrink-0 group-hover:bg-[#a5400e]/20 transition-colors">
                        <Check className="text-[#a5400e]" size={12} />
                      </div>
                      <span className="text-xs sm:text-sm text-[#5c2810] group-hover:text-[#a5400e] transition-colors">{principle}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        {/* Section Kinésiologie - Design alterné avec Terracotta */}
        <motion.div
          className="relative mb-8 sm:mb-12"
          initial={shouldReduceMotion ? undefined : { opacity: 0, y: 20 }}
          whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          <div className="relative bg-gradient-to-br from-[#a5400e] via-[#d46a3e] to-[#a5400e] p-5 sm:p-6 lg:p-8 xl:p-10 rounded-2xl sm:rounded-3xl shadow-[0_20px_60px_rgb(165,64,14,0.25)] overflow-hidden">
            {/* Pattern decoratif en background */}
            <div className="absolute inset-0 opacity-10 pointer-events-none">
              <div className="absolute top-0 left-0 w-48 h-48 sm:w-64 sm:h-64 bg-white rounded-full blur-3xl transform -translate-x-1/2 -translate-y-1/2" />
              <div className="absolute bottom-0 right-0 w-64 h-64 sm:w-96 sm:h-96 bg-white rounded-full blur-3xl transform translate-x-1/3 translate-y-1/3" />
            </div>

            <div className="relative">
              {/* Header */}
              <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-4 mb-5 sm:mb-6">
                <div className="w-12 h-12 sm:w-14 sm:h-14 rounded-full bg-white/20 backdrop-blur-sm flex items-center justify-center shrink-0">
                  <Brain className="text-white" size={24} />
                </div>
                <div>
                  <span className="text-xs uppercase tracking-[0.2em] text-white/70 font-medium">
                    Approche Complémentaire
                  </span>
                  <h3 className="text-xl sm:text-2xl lg:text-3xl xl:text-4xl font-serif text-white mt-1">
                    {kinesiologyContent.title}
                  </h3>
                </div>
              </div>

              {/* Content */}
              <div className="grid lg:grid-cols-2 gap-5 sm:gap-6 lg:gap-8">
                <div>
                  <p className="text-sm sm:text-base lg:text-lg text-white/95 leading-relaxed mb-3">
                    {kinesiologyContent.description}
                  </p>
                  <p className="text-xs sm:text-sm lg:text-base text-white/80 leading-relaxed whitespace-pre-line">
                    {kinesiologyContent.detail}
                  </p>
                </div>

                <div className="bg-white/10 backdrop-blur-sm p-4 sm:p-5 lg:p-6 rounded-xl sm:rounded-2xl border border-white/20">
                  <h4 className="text-sm sm:text-base lg:text-lg font-serif text-white mb-3 flex items-center gap-2">
                    <Heart size={18} />
                    Bénéfices de la Kinésiologie
                  </h4>
                  <div className="space-y-2 sm:space-y-3">
                    {kinesiologyContent.benefits.map((benefit, index) => (
                      <div
                        key={index}
                        className="flex items-start space-x-3 group"
                      >
                        <div className="w-5 h-5 sm:w-6 sm:h-6 rounded-full bg-white/20 flex items-center justify-center flex-shrink-0 group-hover:bg-white/30 transition-colors">
                          <Check className="text-white" size={12} />
                        </div>
                        <span className="text-xs sm:text-sm text-white/90 group-hover:text-white transition-colors">{benefit}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </motion.div>

        <motion.div
          className="grid md:grid-cols-2 gap-6 sm:gap-8 mb-8 sm:mb-12"
          initial={shouldReduceMotion ? undefined : { opacity: 0, y: 20 }}
          whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.5 }}
        >
          <article className="bg-gradient-to-br from-[#ead0b7] via-[#f4e4d4] to-[#f9f2e9] p-5 sm:p-8 rounded-2xl sm:rounded-3xl border border-[#a5400e]/25 border-t-4 border-t-[#a5400e] shadow-[0_8px_30px_rgb(165,64,14,0.10)]">
            <div className="flex items-center gap-3 sm:gap-4 mb-5">
              <div className="w-11 h-11 sm:w-12 sm:h-12 shrink-0 rounded-full bg-[#a5400e] flex items-center justify-center">
                <Sparkles className="text-white" size={24} aria-hidden="true" />
              </div>
              <h3 className="text-xl sm:text-2xl font-serif font-medium text-[#5c2810] leading-snug">Thérapeute spécialisée en visualisations</h3>
            </div>
            <div className="space-y-3 text-sm sm:text-base text-[#5c2810] leading-relaxed">
              <p>
                Les visualisations guidées offrent un espace pour explorer votre monde intérieur, mettre des
                images sur vos ressentis et accueillir vos émotions sans jugement.
              </p>
              <p>
                Le cerveau ne fait pas la différence entre un événement vécu ou fortement imaginé.
                C'est sur cette capacité que nous nous appuyons comme facteur de changement, pour explorer
                de nouvelles voies possibles et vous aider à sortir de vos schémas limitants.
              </p>
            </div>
          </article>
          <article className="bg-white p-5 sm:p-8 rounded-2xl sm:rounded-3xl border border-[#a5400e]/20 border-t-4 border-t-[#c58d6e] shadow-[0_8px_30px_rgb(165,64,14,0.08)]">
            <div className="flex items-center gap-3 sm:gap-4 mb-5">
              <div className="w-11 h-11 sm:w-12 sm:h-12 shrink-0 rounded-full bg-[#f2e1d4] flex items-center justify-center">
                <Heart className="text-[#a5400e]" size={24} aria-hidden="true" />
              </div>
              <h3 className="text-xl sm:text-2xl font-serif font-medium text-[#5c2810] leading-snug">Accompagnement en addiction</h3>
            </div>
            <p className="text-sm sm:text-base text-[#7a4a3a] leading-relaxed">
              {"Un espace d’écoute, sans jugement, pour explorer les émotions et les schémas qui accompagnent les conduites addictives. Je vous accompagne à votre rythme pour mieux comprendre ce que vous vivez et mobiliser vos ressources dans votre démarche de changement. Cet accompagnement de bien-être vient en complément d’un suivi par un professionnel de santé en addictologie et ne le remplace pas."}
            </p>
          </article>
        </motion.div>

        <motion.div
          className="bg-white/70 p-5 sm:p-8 lg:p-10 rounded-2xl sm:rounded-3xl border border-[#a5400e]/15 mb-8 sm:mb-12"
          initial={shouldReduceMotion ? undefined : { opacity: 0, y: 20 }}
          whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.15 }}
          transition={{ duration: 0.5 }}
        >
          <div className="flex items-center gap-3 mb-6">
            <BookOpen className="text-[#a5400e] shrink-0" size={24} aria-hidden="true" />
            <h3 className="text-xl sm:text-2xl lg:text-3xl font-serif text-[#5c2810]">Formations en cours</h3>
          </div>
          <p className="text-sm sm:text-base text-[#7a4a3a] leading-relaxed mb-6">
            Deux cursus de longue durée, déjà bien engagés, enrichissent ma pratique : la psychanalyse
            et la naturopathie fonctionnelle et causaliste. Ces formations approfondies nourrissent
            ma compréhension de la personne et mon accompagnement.
          </p>
          <div className="grid md:grid-cols-2 gap-6 sm:gap-8">
            <div className="border-l-2 border-[#a5400e]/30 pl-5">
              <h4 className="flex items-center gap-2 text-lg sm:text-xl font-serif text-[#a5400e] mb-3">
                <Brain size={20} aria-hidden="true" /> La psychanalyse
              </h4>
              <p className="text-sm sm:text-base text-[#7a4a3a] leading-relaxed">
                Elle apporte une connaissance riche et approfondie de la manière dont se construit un
                inconscient, des obstacles et des traumatismes qu'il rencontre et des effets durables
                qu'ils produisent sur un être.
              </p>
            </div>
            <div className="border-l-2 border-[#a5400e]/30 pl-5">
              <h4 className="flex items-start gap-2 text-lg sm:text-xl font-serif text-[#a5400e] mb-3">
                <Leaf className="shrink-0 mt-1" size={20} aria-hidden="true" /> La naturopathie fonctionnelle et causaliste
              </h4>
              <p className="text-sm sm:text-base text-[#7a4a3a] leading-relaxed">
                Les différents systèmes du corps étant étroitement liés, comprendre leur fonctionnement
                profond et leurs interactions est indispensable pour un accompagnement global de la personne.
              </p>
            </div>
          </div>
        </motion.div>

        {/* Philosophie de soins - Section finale élégante */}
        <motion.div
          className="relative"
          initial={shouldReduceMotion ? undefined : { opacity: 0, y: 20 }}
          whileInView={shouldReduceMotion ? undefined : { opacity: 1, y: 0 }}
          viewport={{ once: true, amount: 0.2 }}
          transition={{ duration: 0.5, delay: 0.2 }}
        >
          <div className="bg-[#F5F0E8] p-5 sm:p-6 lg:p-8 xl:p-10 rounded-2xl sm:rounded-3xl border-2 border-dashed border-[#a5400e]/30 text-center">
            <div className="w-12 h-12 sm:w-14 sm:h-14 lg:w-16 lg:h-16 mx-auto mb-4 sm:mb-6 rounded-full bg-gradient-to-br from-[#a5400e] to-[#e8a87c] flex items-center justify-center shadow-lg">
              <Sparkles className="text-white" size={24} />
            </div>

            <h4 className="text-lg sm:text-xl lg:text-2xl xl:text-3xl font-serif text-[#5c2810] mb-3 sm:mb-4">
              {"Philosophie d’accompagnement"}
            </h4>
            <p className="text-sm sm:text-base lg:text-lg text-[#7a4a3a] max-w-3xl mx-auto leading-relaxed">
              En tant que traductrice de l'inconscient, je considère chaque personne comme <span className="text-[#a5400e] font-medium">unique</span>, avec son histoire
              et ses besoins spécifiques. Mon approche vise à identifier les causes profondes des déséquilibres
              plutôt que de simplement m'attarder sur les symptômes résultant d'une cascade en chaîne.
            </p>
          </div>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default MethodSection;
